import { useFolderStructure } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import styled from 'styled-components';

import {
  useFolderViewActions,
  useStorySelection,
} from './storyFolderViewContext';

import type { FC } from 'react';

const BulkActionBar: FC = observer(() => {
  const selection = useStorySelection();
  const actions = useFolderViewActions();
  const store = useFolderStructure();

  const spansFolders = useMemo(() => {
    const seen = new Set<string | null>();
    for (const id of selection.selectedIds) {
      seen.add(store.folderIdForStory(id));
      if (seen.size > 1) return true;
    }
    return false;
  }, [selection.selectedIds, store]);

  if (selection.size === 0) return null;

  return (
    <Bar
      role="toolbar"
      aria-label="Bulk story actions"
      data-testid="bulk-action-bar"
    >
      <Count>
        <CountNumber>{selection.size}</CountNumber> selected
        {spansFolders ? ' across folders' : ''}
      </Count>

      <GhostButton
        type="button"
        onClick={() => selection.clear()}
        data-testid="bulk-clear"
      >
        Clear
      </GhostButton>

      <Divider />

      <PrimaryButton
        type="button"
        onClick={() =>
          actions.openMoveDialog(Array.from(selection.selectedIds))
        }
        data-testid="bulk-move-to-folder"
      >
        Move to Folder
      </PrimaryButton>
      <DestructiveButton
        type="button"
        onClick={() =>
          actions.openBulkDeleteDialog(Array.from(selection.selectedIds))
        }
        data-testid="bulk-delete"
      >
        Delete
      </DestructiveButton>
    </Bar>
  );
});

export default BulkActionBar;

const Bar = styled.div`
  /* Floating toolbar pinned to the bottom-center of the page. */
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 6px 6px 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--elevation-lg);
`;

const Count = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  padding-right: var(--spacing-xs);
`;

const CountNumber = styled.span`
  color: var(--text-primary);
  font-weight: var(--font-weight-semi-bold);
`;

const Divider = styled.span`
  width: 1px;
  height: 16px;
  margin: 0 2px;
  background: var(--border);
  flex-shrink: 0;
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border-radius: var(--radius-input);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;
`;

const GhostButton = styled.button`
  ${buttonBase}
  background: none;
  border: none;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);

  &:hover {
    background: var(--surface-elevated);
    color: var(--text-secondary);
  }
`;

const PrimaryButton = styled.button`
  ${buttonBase}
  background: var(--primary);
  color: var(--background);
  border: none;
  font-weight: var(--font-weight-semi-bold);

  &:hover {
    background: var(--primary-hover);
  }
`;

const DestructiveButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: var(--destructive);
  border: 1px solid var(--destructive);
  font-weight: var(--font-weight-medium);

  &:hover {
    background: color-mix(in srgb, var(--destructive) 12%, transparent);
  }
`;
